import { useState, useEffect } from 'react'
import Modal from './Modal'
import Button from './Button'
import api from '../services/api'

export const ManageUserOrganizationsModal = ({ user, isOpen, onClose, onSuccess }) => {
  const [orgs, setOrgs] = useState(user?.organizations ?? [])
  const [allOrgs, setAllOrgs] = useState([])
  const [addOrgId, setAddOrgId] = useState('')
  const [addRole, setAddRole] = useState('member')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!isOpen) return
    setOrgs(user?.organizations ?? [])
    setAddOrgId('')
    setAddRole('member')
    setError(null)

    api.getAdminOrganizations({ per_page: 200 })
      .then(res => setAllOrgs(res.data.data ?? res.data))
      .catch(() => setError('Failed to load organizations'))
  }, [isOpen])

  const handleAdd = async () => {
    if (!addOrgId) return
    setLoading(true)
    setError(null)
    try {
      const res = await api.addUserToOrganization(user.id, addOrgId, addRole)
      setOrgs(res.data.organizations)
      setAddOrgId('')
      onSuccess?.()
    } catch (e) {
      setError(e.response?.data?.message ?? 'Failed to add organization')
    } finally {
      setLoading(false)
    }
  }

  const handleRoleChange = async (orgId, role) => {
    setLoading(true)
    try {
      const res = await api.updateUserOrganizationRole(user.id, orgId, role)
      setOrgs(res.data.organizations)
      onSuccess?.()
    } catch (e) {
      setError(e.response?.data?.message ?? 'Failed to update role')
    } finally {
      setLoading(false)
    }
  }

  const handleRemove = async (orgId) => {
    setLoading(true)
    try {
      const res = await api.removeUserFromOrganization(user.id, orgId)
      setOrgs(res.data.organizations)
      onSuccess?.()
    } catch (e) {
      setError(e.response?.data?.message ?? 'Failed to remove organization')
    } finally {
      setLoading(false)
    }
  }

  if (!user) return null

  const availableOrgs = allOrgs.filter(o => !orgs.find(uo => uo.id === o.id))

  return (
    <Modal isOpen={isOpen} setIsOpen={onClose} width="md:max-w-screen-sm">
      <div>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-full bg-brand bg-opacity-10 flex items-center justify-center shrink-0">
            <i className="fa fa-building text-brand text-xl"></i>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Manage Organizations</h2>
            <p className="text-sm text-gray-500">{user.name}</p>
          </div>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <div className="mb-6 border border-gray-200 rounded-lg divide-y divide-gray-100">
          {orgs.length === 0 ? (
            <p className="text-sm text-gray-500 px-4 py-4">No organization memberships.</p>
          ) : (
            orgs.map(org => (
              <div key={org.id} className="flex items-center justify-between px-4 py-3">
                <span className="text-sm font-medium text-gray-900">{org.name}</span>
                <div className="flex items-center gap-3">
                  <select
                    value={org.pivot?.role ?? 'member'}
                    onChange={e => handleRoleChange(org.id, e.target.value)}
                    className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 text-gray-700 focus:outline-none focus:ring-2 focus:ring-brand/50 min-w-[110px]"
                    disabled={loading}
                  >
                    <option value="member">Member</option>
                    <option value="admin">Org Admin</option>
                  </select>
                  <button
                    onClick={() => handleRemove(org.id)}
                    className="text-sm text-red-500 hover:text-red-700 disabled:opacity-50"
                    disabled={loading}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="border-t border-gray-100 pt-5">
          <p className="text-sm font-semibold text-gray-700 mb-3">Add to organization</p>
          <div className="flex gap-2">
            <select
              value={addOrgId}
              onChange={e => setAddOrgId(e.target.value)}
              className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-brand/50"
              disabled={loading}
            >
              <option value="">Select organization…</option>
              {availableOrgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
            <select
              value={addRole}
              onChange={e => setAddRole(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-brand/50 min-w-[110px]"
              disabled={loading}
            >
              <option value="member">Member</option>
              <option value="admin">Org Admin</option>
            </select>
            <Button
              type="button"
              onClick={handleAdd}
              disabled={loading || !addOrgId}
            >
              {loading ? <i className="fa fa-spinner fa-spin"></i> : 'Add'}
            </Button>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <Button type="button" variant="secondary-ghost" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  )
}
